import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { track, identify, trackSignUp, EVENTS } from "@/lib/analytics";
import ContactButton from "@/components/ContactButton";
import { BrandLogo, BrandLogoPrimary, JourneyLine } from "@/components/Brand";

const CALENDLY_URL = "https://calendly.com/ashlee-asvmedicalservices";

/** Open the Calendly popup widget. Loads the widget JS lazily on first
 *  call (idempotent — shared with GlobalCalendlyBadge's loader tag). */
function openCalendlyPopup() {
    if (typeof window === "undefined") return;
    const fire = () => {
        try {
            window.Calendly?.initPopupWidget?.({ url: CALENDLY_URL });
        } catch (_e) { /* widget will retry on next click */ }
    };
    if (window.Calendly?.initPopupWidget) {
        fire();
        return;
    }
    // Ensure the Calendly CSS + JS are present, then open.
    const cssHref = "https://assets.calendly.com/assets/external/widget.css";
    if (!document.querySelector(`link[href="${cssHref}"]`)) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = cssHref;
        document.head.appendChild(link);
    }
    let script = document.querySelector(`script[data-calendly-loader="true"]`);
    if (!script) {
        script = document.createElement("script");
        script.src = "https://assets.calendly.com/assets/external/widget.js";
        script.async = true;
        script.dataset.calendlyLoader = "true";
        document.body.appendChild(script);
    }
    const wait = () => (window.Calendly?.initPopupWidget ? fire() : setTimeout(wait, 60));
    wait();
}
import SearchMemoryLane from "@/components/SearchMemoryLane";
import SeoHead from "@/components/SeoHead";
import { useAuth } from "@/lib/auth";
import { useCountry } from "@/lib/country";
import { SUPPORTED_COUNTRIES, SUPPORTED_LANGUAGES, countryMeta } from "@/lib/countries";
import { getInternationalResources, INTL_COUNTRIES_WITH_RESOURCES } from "@/lib/internationalResources";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import CalendlyInlineEmbed from "@/components/CalendlyInlineEmbed";
import ActivationSection from "@/components/activation/ActivationSection";
import {
    Heart, Compass, MapPin, MilestoneIcon, ArrowRight,
    Eye, Scale3D, Library, Map, ChevronRight, ChevronDown,
    Brain, ClipboardList, Home as HomeIcon, HandHeart, Pin,
    Sprout, Stethoscope, BookOpen, Users,
    Trophy, Check, Sparkles, MessageCircle, KeyRound, Hospital, Mail, X, Eye as EyeIcon, EyeOff
} from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

const NAV = [
    { key: "landing.nav.home", to: "/", testid: "nav-link-home", label: "Home" },
    { key: "landing.nav.about", to: "/about", testid: "nav-link-about", label: "About" },
    { key: "nav.organizations", to: "/organizations", testid: "nav-link-organizations", label: "Health Organizations" },
    { key: "nav.partners", to: "/partners", testid: "nav-link-partners", label: "Partners" },
];

// Mirrors the app's FAST stage palette (sage → sand → lavender → coral for crisis)
const ROADMAP_STEPS = [
    {
        icon: Eye,
        title: "Early stage",
        text: "Memory slips, missed bills, getting lost in familiar places. Start the conversation, know what to do next.",
        tone: "early",
    },
    {
        icon: Brain,
        title: "Middle stage",
        text: "Wandering, sleep changes, agitation. Lean on routines and the strategies that worked last time.",
        tone: "middle",
    },
    {
        icon: HandHeart,
        title: "Late stage",
        text: "Comfort care, hospice decisions, family at the bedside. We help you focus on dignity and presence.",
        tone: "late",
    },
    {
        icon: Pin,
        title: "Crisis moments",
        text: "Aggression, falls, an ER visit. One-tap call lines and a clear next step — no scrolling for help.",
        tone: "crisis",
    },
];

const TONE_CARDS = {
    early: { bg: "bg-stage_early", text: "text-stage_early-dark", icon_bg: "bg-white text-stage_early-dark" },
    middle: { bg: "bg-stage_middle", text: "text-stage_middle-dark", icon_bg: "bg-white text-stage_middle-dark" },
    late: { bg: "bg-stage_late", text: "text-stage_late-dark", icon_bg: "bg-white text-stage_late-dark" },
    crisis: { bg: "bg-coral-light", text: "text-coral-dark", icon_bg: "bg-white text-coral-dark" },
};

/**
 * Shared landing chrome — header used on `/` and `/about`. Warm ivory
 * with a thin dusty-teal accent line and a soft eyebrow — pretty,
 * calm, decidedly not gold. Sign-in modal state is passed through so
 * clicking "Sign in" opens the same quick-auth panel on any page.
 */
export function LandingHeader({ onOpenSignIn, t }) {
    return (
        <header
            data-clarity-unmask="true"
            className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#EBE7DD] shadow-[0_8px_24px_-18px_rgba(31,38,36,0.25)]"
        >
            {/* Thin journey-gradient accent line at the very top. */}
            <div
                aria-hidden
                className="h-[3px] w-full"
                style={{ background: "linear-gradient(90deg, #4F7F95 0%, #1D8A85 55%, #12776F 100%)" }}
            />
            {/* Eyebrow — quiet, editorial. */}
            <div className="border-b border-[#F2EFE9]">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-1.5 flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#12776F]">
                        Memory Lane · Care Navigator
                    </span>
                    <span aria-hidden className="flex-1 h-px bg-[#EBE7DD]" />
                    <span className="hidden sm:inline text-[10px] uppercase tracking-[0.18em] font-semibold text-[#6E7A74]">
                        {t("Built by an NP · For caregivers")}
                    </span>
                </div>
            </div>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2 sm:gap-4">
                <Link to="/" className="flex items-center shrink-0" data-testid="landing-brand">
                    <BrandLogo className="h-6 sm:h-7" />
                </Link>
                <nav className="hidden md:flex items-center gap-1">
                    {NAV.map(n => {
                        const href = n.anchor ? `${n.to}${n.anchor}` : n.to;
                        return (
                            <Link key={n.key} to={href} data-testid={n.testid}
                                className="text-sm font-medium text-[#4C5551] hover:text-[#12776F] px-3 py-2 rounded-2xl hover:bg-teal-soft/60 transition-colors">
                                {n.label ? t(n.label) : t(n.key)}
                            </Link>
                        );
                    })}
                </nav>
                <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <LanguageSwitcher />
                    <div className="hidden sm:inline-flex items-center px-1">
                        <ContactButton label={t("landing.contact")} />
                    </div>
                    <button
                        type="button"
                        onClick={onOpenSignIn}
                        data-testid="landing-signin"
                        className="inline-flex items-center whitespace-nowrap text-sm font-medium text-[#4C5551] hover:text-[#12776F] px-2.5 sm:px-3 py-2 rounded-2xl hover:bg-teal-soft/60 transition-colors"
                    >
                        {t("landing.signIn")}
                    </button>
                    <Link to="/beta" data-testid="landing-beta-cta"
                        className="inline-flex items-center gap-2 whitespace-nowrap bg-teal text-white hover:bg-teal-dark rounded-2xl px-3 sm:px-4 py-2 text-sm font-semibold min-h-[44px] transition-colors shadow-sm">
                        <span className="hidden sm:inline">{t("landing.tryFree")}</span>
                        <span className="sm:hidden">{t("landing.tryShort")}</span>
                    </Link>
                </div>
            </div>
        </header>
    );
}

/* Shared footer used on `/` and `/about`. */
export function LandingFooter({ t }) {
    return (
        <footer data-clarity-unmask="true" className="py-12 border-t border-[#EBE7DD]" data-testid="landing-footer">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-6">
                <div className="flex flex-col items-center gap-1">
                    <BrandLogo className="h-7" />
                </div>
                <div
                    className="text-center max-w-2xl mx-auto space-y-1"
                    data-testid="landing-footer-credo"
                >
                    <p className="font-heading text-[#1F2624] text-base">
                        {t("landing.footer.credo1")} <span className="text-teal-dark font-semibold">{t("landing.footer.credoAccent")}</span> {t("landing.footer.credo2")}
                    </p>
                    <p className="text-sm text-[#4C5551] mt-3 leading-relaxed">
                        {t("landing.footer.builtBy")}<br />
                        {t("landing.footer.designedWith")}<br />
                        {t("landing.footer.builtToStrengthen")}
                    </p>
                </div>
                <p
                    className="text-xs text-[#6E7A74] leading-relaxed max-w-3xl mx-auto text-center pt-2"
                    data-testid="landing-footer-disclaimer"
                >
                    {t("landing.footer.disclaimer")}
                </p>
                <div
                    className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-[#4C5551] pt-4 border-t border-cream-dark/50"
                    data-testid="landing-footer-tools"
                >
                    <span className="font-semibold text-[#1F2624]">{t("Free tools:")}</span>
                    <Link to="/wellness-check" className="hover:text-teal-dark" data-testid="footer-wellness-check-link">{t("Caregiver Wellness Check")}</Link>
                    <Link to="/tools/journey-quiz" className="hover:text-teal-dark" data-testid="footer-journey-quiz-link">{t("Where Are We on the Journey?")}</Link>
                    <Link to="/tools/guide-check" className="hover:text-teal-dark" data-testid="footer-guide-check-link">{t("Medicare GUIDE Eligibility Check")}</Link>
                    <Link to="/tools" className="hover:text-teal-dark" data-testid="footer-tools-link">{t("All tools")}</Link>
                </div>
                <div className="flex flex-wrap justify-between gap-3 text-sm text-[#4C5551] pt-4 border-t border-cream-dark/50">
                    <p>{t("© Memory Lane Care Navigator — built with care.")}</p>
                    <div className="flex flex-wrap gap-4">
                        <Link to="/feedback" className="hover:text-teal-dark">{t("Feedback")}</Link>
                        <Link to="/resources" className="hover:text-teal-dark">{t("Resources")}</Link>
                        <span aria-hidden="true">·</span>
                        <Link to="/library" className="hover:text-teal-dark" data-testid="footer-library-link">{t("Knowledge Library")}</Link>
                        <Link to="/demo" className="hover:text-teal-dark">{t("See how it works")}</Link>
                        <Link to="/beta" className="hover:text-teal-dark">{t("Try Memory Lane")}</Link>
                        <Link to="/partners" className="hover:text-teal-dark">{t("For Healthcare Partners")}</Link>
                        <Link to="/about" className="hover:text-teal-dark">{t("About")}</Link>
                        <Link to="/terms" className="hover:text-teal-dark">{t("Terms")}</Link>
                        <Link to="/privacy" className="hover:text-teal-dark">{t("Privacy")}</Link>
                        <Link to="/cookies" data-testid="landing-footer-cookies" className="hover:text-teal-dark">{t("Cookies")}</Link>
                        <Link to="/security" data-testid="landing-footer-security" className="hover:text-teal-dark">{t("Security")}</Link>
                        <Link to="/refer" data-testid="landing-footer-refer" className="hover:text-teal-dark">{t("Refer a caregiver")}</Link>
                        <ContactButton label={t("Contact")} />
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default function Landing() {
    const [count, setCount] = useState(null);
    const [signInOpen, setSignInOpen] = useState(false);
    // Audience selector personalizes the hero IN PLACE (no navigation) —
    // orgs used to be sent away to /organizations before seeing any proof.
    const [audience, setAudience] = useState(() => {
        // Org-intent arrivals (LinkedIn / org outreach links) get the org hero
        // without a click: ?audience=org|organization, utm_medium=org, or
        // utm_source=linkedin. URL signal wins over the remembered choice.
        try {
            const q = new URLSearchParams(window.location.search);
            const explicit = (q.get("audience") || "").toLowerCase();
            const orgIntent = explicit === "org" || explicit === "organization"
                || (q.get("utm_medium") || "").toLowerCase() === "org"
                || (q.get("utm_source") || "").toLowerCase() === "linkedin";
            if (explicit === "caregiver" || explicit === "family") return "caregiver";
            if (orgIntent) {
                setTimeout(() => track("audience_selected", { audience: "organization", page: "landing", method: "auto_url" }), 0);
                return "organization";
            }
            return localStorage.getItem("ml_audience") || "caregiver";
        } catch { return "caregiver"; }
    });
    const pickAudience = (a) => {
        setAudience(a);
        try { localStorage.setItem("ml_audience", a); } catch { /* private mode */ }
        track("audience_selected", { audience: a, page: "landing" });
    };
    const isOrg = audience === "organization";
    const hk = isOrg ? "landing.heroOrg" : "landing.hero";
    const { t } = useCountry();

    useEffect(() => {
        api.get("/beta-signup/count").then(r => setCount(r.data.count)).catch(() => { });
    }, []);

    return (
        <div className="min-h-screen bg-cream" id="top">
            <SeoHead
                title="Memory Lane Care — The Continuity Platform for Dementia"
                description="Memory Lane is the trusted front door for memory and cognitive care — personalized guidance from the first signs of memory change through every stage of care. Includes a personalized roadmap, AI navigator Lane, GUIDE eligibility, Continuity Passport, and community resources for families noticing memory concerns, navigating MCI or a new Alzheimer's diagnosis, or living with dementia."
                canonicalPath="/"
                articleData={{
                    "@context": "https://schema.org",
                    "@graph": [
                        {
                            "@type": "Organization",
                            name: "Memory Lane Care",
                            url: "https://memorylanecare.net/",
                            logo: "https://memorylanecare.net/logo192.png",
                            description: "A continuity platform for memory and cognitive care — personalized roadmaps from the first signs of memory change, AI navigation, GUIDE program support, and continuity-of-care tools for families noticing memory concerns, navigating MCI or a new diagnosis, living with dementia, or preparing for advanced care.",
                            founder: {
                                "@type": "Person",
                                name: "Ashlee Skabla Velez, APRN",
                                jobTitle: "Nurse Practitioner, Founder",
                            },
                            sameAs: [
                                "https://memorylanecare.net/",
                            ],
                        },
                        {
                            "@type": "WebSite",
                            url: "https://memorylanecare.net/",
                            name: "Memory Lane Care Navigator",
                            inLanguage: "en-US",
                            publisher: { "@type": "Organization", name: "Memory Lane Care" },
                        },
                    ],
                }}
            />
            <LandingHeader onOpenSignIn={() => setSignInOpen(true)} t={t} />

            {/* Animated 30-second signature hero — emotional first impression
                that doubles as the brand-line moment. CSS-only, so no
                external video CDN, fail-safe under any network. Skip button
                scrolls past it to the main hero for keyboard users. */}
            {/* AnimatedHero removed — it said the same thing as the main
                hero headline below, making the landing feel redundant.
                Main hero is now the first thing visitors see. */}

            {/* Hero — caregiver-first voice. Lead with their pain, not us.
                Two clear buttons (Start Free, See How It Works) — no inline
                email form, no founder card crowding the CTA stack.
                data-clarity-unmask: this section is pure marketing copy
                (no caregiver-entered data), so unmasking it makes Clarity
                replays useful for hero/CTA conversion analysis. */}
            <section id="landing-main-hero" data-clarity-unmask="true" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-14 pb-10 md:pb-16">
                {/* Audience toggle — helps providers/orgs peel off before
                    the caregiver hero pulls them into the wrong flow. */}
                <div className="mb-8 flex flex-wrap items-center justify-center gap-2 md:gap-3" data-testid="landing-audience-toggle">
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#4C5551]">{t("I'm a")}</span>
                    <div className="inline-flex items-center rounded-full border border-[#D6C9AE] bg-white/80 backdrop-blur-sm p-1 shadow-sm">
                        <button
                            type="button"
                            aria-pressed={!isOrg}
                            onClick={() => pickAudience("caregiver")}
                            data-testid="audience-caregiver"
                            className={`text-xs md:text-sm px-3.5 py-1.5 rounded-2xl transition-colors ${!isOrg ? "font-semibold bg-teal-dark text-white shadow-sm" : "font-medium text-[#3B4A57] hover:text-teal-dark hover:bg-white/70"}`}
                        >
                            {t("Caregiver or family")}
                        </button>
                        <button
                            type="button"
                            aria-pressed={isOrg}
                            onClick={() => pickAudience("organization")}
                            data-testid="audience-organization"
                            className={`text-xs md:text-sm px-3.5 py-1.5 rounded-2xl transition-colors ${isOrg ? "font-semibold bg-ml_blue-dark text-white shadow-sm" : "font-medium text-[#3B4A57] hover:text-teal-dark hover:bg-white/70"}`}
                        >
                            {t("Health organization")}
                        </button>
                        <Link
                            to="/partners"
                            data-testid="audience-partner"
                            className="text-xs md:text-sm font-medium px-3.5 py-1.5 rounded-2xl text-[#3B4A57] hover:text-teal-dark hover:bg-white/70 transition-colors"
                        >
                            {t("Care partner")}
                        </Link>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 gap-10 items-center">
                    <div className="animate-fade-in-up">
                        <div className="mb-5">
                            <BrandLogoPrimary className="h-8 sm:h-10" />
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold tracking-tight leading-[1.05] text-[#12776F]" data-testid="landing-hero-headline">
                            {t(`${hk}.headline1`)} <span className="text-teal-dark">{t(`${hk}.headline2`)}</span>
                        </h1>
                        <p className="mt-4 text-xl sm:text-2xl font-heading font-medium text-[#4C5551] leading-snug" data-testid="landing-hero-subheadline">
                            {t(`${hk}.subhead`)}
                        </p>
                        <p className="mt-5 text-base md:text-lg text-[#4C5551] leading-relaxed max-w-xl" data-testid="landing-hero-body">
                            {t(`${hk}.body`)}
                        </p>

                        {!isOrg && <HeroSignupForm />}

                        {isOrg ? (
                            <div className="mt-7 flex flex-wrap items-center gap-3" data-testid="landing-hero-org-ctas">
                                <Link
                                    to="/organizations"
                                    data-testid="landing-hero-org-explore"
                                    onClick={() => track(EVENTS.CTA_CLICK, { cta: "org_hero_explore", page: "landing" })}
                                    className="ml-btn-primary !min-h-[52px] !px-7 text-base inline-flex items-center gap-2 !bg-ml_blue-dark hover:!bg-ml_blue"
                                >
                                    <Hospital size={17} strokeWidth={1.8} /> {t("landing.heroOrg.exploreCta")}
                                </Link>
                                <Link
                                    to="/book"
                                    data-testid="landing-hero-org-book"
                                    onClick={() => track(EVENTS.CTA_CLICK, { cta: "org_hero_book", page: "landing" })}
                                    className="ml-btn-secondary !min-h-[52px] !px-6 text-base inline-flex items-center gap-2"
                                >
                                    {t("landing.heroOrg.bookCta")}
                                </Link>
                            </div>
                        ) : (
                        <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="landing-hero-ctas">
                            <button
                                type="button"
                                onClick={() => {
                                    track(EVENTS.CTA_CLICK, { cta: "see_how_it_works", page: "landing" });
                                    const el = document.getElementById("landing-tabs")
                                        || document.querySelector("[data-testid='landing-platform-flow']");
                                    el?.scrollIntoView({ behavior: "smooth", block: "start" });
                                    window.dispatchEvent(new CustomEvent("ml-open-how-tab"));
                                }}
                                data-testid="landing-hero-how"
                                className="ml-btn-secondary !min-h-[52px] !px-7 text-base inline-flex items-center gap-2"
                            >
                                {t("landing.hero.howItWorks")}
                            </button>
                            <button
                                type="button"
                                data-testid="landing-hero-for-orgs"
                                onClick={() => { track(EVENTS.CTA_CLICK, { cta: "hero_for_orgs", page: "landing" }); pickAudience("organization"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                                className="!min-h-[52px] !px-6 inline-flex items-center gap-2 rounded-full text-base font-heading font-semibold text-ml_blue-dark bg-white border-2 border-ml_blue-soft hover:bg-ml_blue-light transition-colors"
                            >
                                <Hospital size={17} strokeWidth={1.8} /> {t("For Healthcare Organizations")}
                            </button>
                        </div>
                        )}

                        <CountryWelcomeStrip />
                        <LiveSignupTicker fallbackCount={count} />
                    </div>
                    <div className="relative">
                        {/* Hero image is purposely clickable — it occupies the
                            visual focal point of the hero, and Clarity showed
                            a high dead-click rate on it. Now scrolls to the
                            inline sign-in below so the click is rewarded. */}
                        <button
                            type="button"
                            onClick={() => {
                                track(EVENTS.CTA_CLICK, { cta: "hero_image", page: "landing" });
                                const el = document.getElementById("landing-tabs")
                                    || document.querySelector("[data-testid='hero-signup-form']")
                                    || document.querySelector("[data-testid='landing-platform-flow']");
                                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
                            }}
                            data-testid="hero-image-cta"
                            aria-label="Sign in or sign up — scroll to the form"
                            className="block w-full aspect-[16/9] rounded-3xl overflow-hidden ml-card group focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-dark"
                        >
                            <img
                                src="/brand/hero-home.jpg"
                                alt="A caregiver and her mother reviewing their Memory Lane plan on a tablet at home, connected to their nurse and care team"
                                width="1400"
                                height="788"
                                loading="eager"
                                fetchPriority="high"
                                decoding="async"
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                            />
                        </button>
                        <RotatingQuoteCard />
                    </div>
                </div>
            </section>

            {/* Activation experiences — six no-account demos ("What brought
                you here today?") so visitors get real value before any
                signup ask. Caregiver audience only; orgs keep their flow. */}
            {!isOrg && <ActivationSection />}

            {/* Repositioning (Jul 2026): the continuity story first — where
                journeys begin + two equal audience sections — so the first
                20 seconds read "infrastructure connecting caregivers and
                care teams", not "AI + NP consults". */}
            <JourneyEntryPoints />
            <PlatformFlow />
            <TwoAudiences />

            {/* Inline sign-in — visible on the landing itself so returning
                caregivers don't have to open a modal. Compact horizontal
                card on desktop; stacks on mobile. Posts to the same auth
                context as the modal + /login page. */}
            <InlineSignIn />

            {/* NP consultations reframed: optional white-glove support
                layered on the platform — deliberately last. */}
            <NpSupportBand />

            {/* Marketing content (feature cards, journey overlay, roadmap
                strip, MeetLane, LandingTabs, organisations band, impact,
                final CTA) has moved to `/about`. The landing page stays
                intentionally short: hero → inline sign-in → footer, so
                caregivers can act without a long scroll.

                Kept exports (LandingTabs, MeetLane, etc.) still ship so
                About and any deep-links continue to work. */}

            <LandingFooter t={t} />

            {/* Quick sign-in modal — caregivers don't have to leave the
                landing to log in. Single network call to /api/auth/login. */}
            <QuickSignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
        </div>
    );
}

/* ---- Repositioning sections (Jul 2026) ---------------------------------- */

const ENTRY_POINTS = [
    { label: "First memory concerns", Icon: Eye },
    { label: "Primary care", Icon: Stethoscope },
    { label: "Neurology", Icon: Brain },
    { label: "Diagnosis", Icon: ClipboardList },
    { label: "GUIDE", Icon: Compass },
    { label: "Home Health", Icon: HomeIcon },
    { label: "Hospice", Icon: Heart },
];

export function JourneyEntryPoints() {
    const { t } = useCountry();
    return (
        <section data-clarity-unmask="true" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="landing-entry-points">
            <div className="text-center max-w-3xl mx-auto">
                <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">{t("The journey")}</p>
                <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tight text-[#12776F]" data-testid="landing-entry-points-title">
                    {t("Every dementia journey begins somewhere.")}
                </h2>
                <p className="mt-3 text-base md:text-lg text-[#4C5551] leading-relaxed">
                    {t("Not every family starts with a neurologist. Some start with a worry at the kitchen table, some with a PCP referral, some mid-GUIDE. Wherever you enter, Memory Lane is already there — and it stays while providers, programs, and settings change.")}
                </p>
            </div>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5" data-testid="landing-entry-points-grid">
                {ENTRY_POINTS.map(({ label, Icon }) => (
                    <div key={label} className="ml-card p-3.5 flex flex-col items-center gap-2 text-center" data-testid={`entry-point-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                        <span className="w-9 h-9 rounded-2xl bg-ml_blue-light text-ml_blue-dark flex items-center justify-center">
                            <Icon size={16} strokeWidth={1.8} />
                        </span>
                        <span className="text-xs font-semibold text-[#1F2624] leading-snug">{t(label)}</span>
                    </div>
                ))}
            </div>

            {/* The continuous layer beneath every stage */}
            <div className="mt-3 rounded-3xl bg-teal-dark text-white px-5 py-4 md:py-5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center shadow-[0_8px_24px_rgba(18,84,82,0.25)]" data-testid="landing-continuity-band">
                <Compass size={18} strokeWidth={1.8} className="shrink-0" />
                <p className="font-heading font-semibold text-sm md:text-base">
                    {t("Memory Lane — the layer that stays with the family beneath every stage.")}
                </p>
                <p className="w-full text-xs text-white/75 mt-0.5">
                    {t("Every provider owns an episode of care. Memory Lane owns the journey.")}
                </p>
            </div>
        </section>
    );
}

const FAMILY_USES = [
    { title: "Personalized guidance", body: "Lane understands your loved one's symptoms, behaviors, medications, stage, and care situation to help you understand what may be happening and what to do next." },
    { title: "24/7 support that remembers", body: "Return anytime without retelling the entire story. Lane remembers previous concerns, patterns, and what you have already tried." },
    { title: "The right support at the right time", body: "Find relevant resources, education, programs, services, and caregiver wellness support as needs change." },
    { title: "Less stress before appointments", body: "Turn everything that has happened between visits into a clear summary so you feel prepared and don't have to reconstruct the story from memory." },
    { title: "Continuity through every transition", body: "Keep one evolving family story as care moves across primary care, neurology, GUIDE, hospitals, home health, hospice, and beyond." },
];

const ORG_USES = [
    { title: "Reach families earlier", body: "Support families from the first memory concerns, before specialist referral or formal enrollment." },
    { title: "Improve visit readiness", body: "Receive clearer histories, behavior patterns, caregiver concerns, medication context, and changes between visits." },
    { title: "See the journey between encounters", body: "Add meaningful longitudinal caregiver context to the isolated snapshots available during appointments." },
    { title: "Strengthen caregiver engagement", body: "Extend personalized education, navigation, resources, and wellness support beyond the clinical visit." },
    { title: "Reduce repeated intake burden", body: "Help families carry their evolving story across providers, programs, and care transitions." },
    { title: "Complement existing workflows", body: "Memory Lane supports GUIDE, primary care, neurology, home health, hospice, and other dementia-care programs without attempting to replace the EHR." },
];

const PLATFORM_FLOW = [
    { title: "Family experiences change", body: "A new behavior, a medication change, a hard night, a diagnosis.", Icon: Heart, tone: "bg-teal-soft text-teal-dark" },
    { title: "Memory Lane understands the situation", body: "Symptoms, stage, history, and what you've already tried — one continuous story.", Icon: Brain, tone: "bg-stage_late-light text-stage_late-dark" },
    { title: "Lane guides and connects", body: "Personalized next steps, plus the right education, resources, and programs at the right time.", Icon: Compass, tone: "bg-yellow-light text-yellow-dark" },
    { title: "Care teams receive better information", body: "Better-prepared caregivers and structured longitudinal context that supports more informed, efficient care.", Icon: Stethoscope, tone: "bg-ml_blue-light text-ml_blue-dark" },
    { title: "Better continuity at every stage", body: "Across primary care, neurology, GUIDE, home health, hospice — and every transition between.", Icon: Sprout, tone: "bg-sage-light text-sage-dark" },
];

export function PlatformFlow() {
    const { t } = useCountry();
    return (
        <section data-clarity-unmask="true" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-16" data-testid="landing-platform-flow">
            <div className="ml-card p-6 md:p-9 bg-white">
                <div className="text-center max-w-3xl mx-auto">
                    <p className="text-sm uppercase tracking-wider font-semibold text-teal-dark mb-2">{t("How it works")}</p>
                    <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tight text-[#12776F]" data-testid="landing-platform-flow-title">
                        {t("One platform, from a hard night to a better visit.")}
                    </h2>
                </div>
                <div className="mt-8 max-w-2xl mx-auto">
                    {PLATFORM_FLOW.map(({ title, body, Icon, tone }, i) => (
                        <React.Fragment key={title}>
                            <div className="flex items-start gap-4" data-testid={`platform-flow-step-${i + 1}`}>
                                <span className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${tone}`}>
                                    <Icon size={18} strokeWidth={1.8} />
                                </span>
                                <div>
                                    <h3 className="font-heading font-semibold text-lg text-[#1F2624]">{t(title)}</h3>
                                    <p className="text-sm text-[#4C5551] leading-relaxed">{t(body)}</p>
                                </div>
                            </div>
                            {i < PLATFORM_FLOW.length - 1 && (
                                <div className="flex justify-start pl-[18px] py-1.5">
                                    <ChevronDown size={18} strokeWidth={2} className="text-teal-light" />
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function TwoAudiences() {
    const { t } = useCountry();
    return (
        <section data-clarity-unmask="true" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-16" data-testid="landing-two-audiences">
            <div className="text-center max-w-3xl mx-auto mb-8">
                <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tight text-[#12776F]" data-testid="landing-two-audiences-title">
                    {t("The same continuity, on both sides of care.")}
                </h2>
                <p className="mt-3 text-base md:text-lg text-[#4C5551] leading-relaxed">
                    {t("Families get daily guidance and one continuous story. Care navigators, GUIDE programs, neurology clinics, home health, hospice, and health systems get engaged caregivers and better-prepared visits — from the same shared story.")}
                </p>
            </div>
            <div className="grid md:grid-cols-2 gap-5 items-stretch">
                {/* Families */}
                <div className="ml-card p-6 md:p-7 bg-gradient-to-br from-teal-soft/60 via-white to-white !border-teal-light/70 flex flex-col" data-testid="landing-families-card">
                    <div className="flex items-center gap-3">
                        <span className="w-11 h-11 rounded-2xl bg-teal text-white flex items-center justify-center shrink-0">
                            <Heart size={19} strokeWidth={1.8} />
                        </span>
                        <div>
                            <h3 className="font-heading font-bold text-xl md:text-2xl text-[#12776F]">
                                {t("For families")}
                            </h3>
                            <p className="text-sm text-teal-dark font-medium">{t("Less uncertainty. More confidence at every stage.")}</p>
                        </div>
                    </div>
                    <ul className="mt-5 space-y-3.5 flex-1" data-testid="landing-families-list">
                        {FAMILY_USES.map(({ title, body }) => (
                            <li key={title} className="flex items-start gap-2.5">
                                <Check size={17} strokeWidth={2.2} className="text-teal-dark mt-1 shrink-0" />
                                <span className="text-base text-[#1F2624]">
                                    <strong className="font-heading font-semibold">{t(title)}.</strong>{" "}
                                    <span className="text-[#4C5551]">{t(body)}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-4 text-sm text-[#4C5551] leading-relaxed font-medium" data-testid="landing-families-mechanism-note">
                        {t("Memory Lane does more than keep information together — it reduces the burden of navigating dementia alone.")}
                    </p>
                    <Link to="/login" data-testid="landing-families-cta" onClick={() => track(EVENTS.CTA_CLICK, { cta: "families_start_free", page: "landing" })} className="ml-btn-primary mt-5 self-start !min-h-[48px] !px-6 text-sm">
                        {t("Start Free")} <ArrowRight size={16} strokeWidth={1.8} />
                    </Link>
                </div>

                {/* Healthcare organizations */}
                <div className="ml-card p-6 md:p-7 bg-gradient-to-br from-ml_blue-light/70 via-white to-white !border-ml_blue-soft/70 flex flex-col" data-testid="landing-orgs-card">
                    <div className="flex items-center gap-3">
                        <span className="w-11 h-11 rounded-2xl bg-ml_blue-dark text-white flex items-center justify-center shrink-0">
                            <Hospital size={19} strokeWidth={1.8} />
                        </span>
                        <div>
                            <h3 className="font-heading font-bold text-xl md:text-2xl text-[#12776F]">
                                {t("For healthcare organizations")}
                            </h3>
                            <p className="text-sm text-ml_blue-dark font-medium">{t("Better-supported caregivers. Better-prepared care.")}</p>
                        </div>
                    </div>
                    <ul className="mt-5 space-y-3.5 flex-1" data-testid="landing-orgs-list">
                        {ORG_USES.map(({ title, body }) => (
                            <li key={title} className="flex items-start gap-2.5">
                                <Check size={17} strokeWidth={2.2} className="text-ml_blue-dark mt-1 shrink-0" />
                                <span className="text-base text-[#1F2624]">
                                    <strong className="font-heading font-semibold">{t(title)}.</strong>{" "}
                                    <span className="text-[#4C5551]">{t(body)}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-4 text-sm text-[#4C5551] leading-relaxed font-medium" data-testid="landing-orgs-outcome-note">
                        {t("Memory Lane helps organizations understand what happens between visits — so teams can spend less time reconstructing the past and more time responding to what families need now.")}
                    </p>
                    <p className="mt-2 text-xs text-[#6E7A74] leading-relaxed" data-testid="landing-orgs-privacy-note">
                        {t("Information is shared through secure, permission-based connections designed to preserve caregiver ownership and trust.")}
                    </p>
                    <Link to="/organizations" data-testid="landing-orgs-cta" className="mt-5 self-start inline-flex items-center gap-2 rounded-2xl bg-ml_blue-dark text-white hover:bg-ml_blue transition-colors px-6 py-3 min-h-[48px] text-sm font-heading font-semibold">
                        {t("For Healthcare Organizations")} <ArrowRight size={16} strokeWidth={1.8} />
                    </Link>
                </div>
            </div>
        </section>
    );
}

export function NpSupportBand() {
    const { t } = useCountry();
    return (
        <section data-clarity-unmask="true" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12" data-testid="landing-np-support">
            <div className="ml-card p-6 md:p-8 bg-gradient-to-br from-yellow-light/70 via-white to-teal-soft/30 !border-yellow-soft">
                <div className="md:flex items-start gap-6">
                    <div className="flex-1">
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-yellow-dark">
                            {t("Optional white-glove support")}
                        </p>
                        <h2 className="mt-1 font-heading font-bold text-2xl md:text-3xl text-[#12776F]" data-testid="landing-np-support-title">
                            {t("Need expert guidance while getting started?")}
                        </h2>
                        <p className="mt-3 text-base text-[#4C5551] leading-relaxed max-w-2xl" data-testid="landing-np-support-copy">
                            {t("Some families benefit from meeting with a dementia nurse practitioner for personalized guidance while using Memory Lane. These optional sessions complement — not replace — your existing neurologist, primary care provider, GUIDE program, or healthcare team.")}
                        </p>
                    </div>
                    <div className="mt-5 md:mt-2 shrink-0">
                        <Link
                            to="/navigator?ref=landing_np_band"
                            data-testid="landing-np-support-cta"
                            className="inline-flex items-center gap-2 rounded-2xl bg-white border-2 border-teal-dark text-teal-dark hover:bg-teal-soft/40 transition-colors px-6 py-3 min-h-[48px] text-sm font-heading font-semibold"
                        >
                            <Stethoscope size={16} strokeWidth={1.8} /> {t("Talk to a dementia NP")}
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function LandingTabs() {
    const [tab, setTab] = React.useState("about");
    // Allow external "See How It Works" / "Book a Nurse Navigator" CTAs
    // to deep-link into the right tab.
    React.useEffect(() => {
        const openHow = () => setTab("how");
        const openBook = () => setTab("book");
        window.addEventListener("ml-open-how-tab", openHow);
        window.addEventListener("ml-open-book-tab", openBook);
        return () => {
            window.removeEventListener("ml-open-how-tab", openHow);
            window.removeEventListener("ml-open-book-tab", openBook);
        };
    }, []);
    const tabs = [
        { id: "about", label: "About" },
        { id: "how", label: "How it works" },
        { id: "book", label: "Book a free 15-min walk-through" },
    ];
    return (
        <section
            id="landing-tabs"
            className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14"
            data-testid="landing-tabs"
        >
            {/* Tab pills */}
            <div
                role="tablist"
                aria-label="Learn more about Memory Lane"
                className="flex flex-wrap gap-2 justify-center mb-7"
            >
                {tabs.map((tt) => (
                    <button
                        key={tt.id}
                        type="button"
                        role="tab"
                        aria-selected={tab === tt.id}
                        data-testid={`landing-tab-${tt.id}`}
                        onClick={() => setTab(tt.id)}
                        className={`px-4 py-2 rounded-2xl text-sm font-semibold transition-colors min-h-[40px] ${tab === tt.id
                                ? "bg-teal text-white"
                                : "bg-white text-[#4C5551] hover:bg-cream-dark border border-[#E2E8F0]"
                            }`}
                    >
                        {tt.label}
                    </button>
                ))}
            </div>

            {/* Panels — swap in place, no scrolling */}
            {tab === "about" && (
                <article
                    role="tabpanel"
                    data-testid="landing-panel-about"
                    className="ml-card p-6 md:p-8 bg-white animate-fade-in-up space-y-8"
                >
                    {/* About — single consolidated pitch (was previously
                        two near-duplicate blocks: "What Memory Lane is" and
                        "What we are: The continuity platform"). Merged so
                        the message reads once, clearly. */}
                    <div data-testid="landing-about-pitch">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                            What Memory Lane is
                        </p>
                        <h3 className="font-heading text-2xl md:text-3xl text-[#12776F] mt-1">
                            The continuity platform for memory and cognitive care.
                        </h3>
                        <p className="mt-3 text-[15px] text-[#1F2624] leading-relaxed">
                            Memory Lane is your personalized guide from the first signs of memory change
                            through every stage of care. Whether you&apos;re noticing forgetfulness, navigating
                            MCI or a new diagnosis, living with dementia, or preparing for advanced care, we
                            help families stay organized, prepare for appointments, understand what comes
                            next, and stay connected to the right providers, programs, and resources.
                        </p>
                        <p className="mt-3 text-[15px] text-[#1F2624] leading-relaxed">
                            We don&apos;t replace neurology, hospice, home health, or Medicare&apos;s GUIDE program —{" "}
                            <span className="text-teal-dark font-semibold">we connect them</span>, so caregivers
                            never start from scratch when something changes.
                        </p>
                    </div>

                    {/* "What we are" block removed — merged into the pitch
                        above to avoid the same idea being said twice on
                        screen. */}

                    {/* === Fragmented today vs Memory Lane continuity === */}
                    <div className="pt-2 border-t border-cream-dark" data-testid="about-fragmented-vs-continuity">
                        <p className="text-xs uppercase tracking-wider font-semibold text-coral-dark mb-1">The problem we solve</p>
                        <h3 className="font-heading text-2xl md:text-3xl text-[#12776F]">
                            Dementia care shouldn&apos;t feel fragmented.
                        </h3>
                        <div className="mt-4 grid md:grid-cols-2 gap-4 items-stretch">
                            <div className="rounded-2xl bg-cream-light border border-cream-dark p-4">
                                <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-2">Today&apos;s journey</p>
                                <ol className="space-y-1 text-sm text-[#1F2624]">
                                    {["Diagnosis", "Neurology", "GUIDE", "Home Health", "Hospice", "Community Resources", "Legal Planning", "Support Groups", "Caregiver Wellness"].map((s, i) => (
                                        <li key={s} className="flex items-center gap-2">
                                            <span className="w-5 h-5 rounded-full bg-cream-dark/60 text-[#4C5551] text-[10px] flex items-center justify-center font-mono">{i + 1}</span>
                                            <span>{s}</span>
                                        </li>
                                    ))}
                                </ol>
                                <p className="mt-3 text-xs text-[#4C5551] italic">Each transition often means another intake. Another history. Another place to start over.</p>
                            </div>
                            <div className="rounded-2xl bg-gradient-to-br from-teal-soft/50 via-cream-light to-stage_early/30 border border-teal-light p-4">
                                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2">With Memory Lane</p>
                                <ul className="space-y-1.5 text-sm text-[#1F2624]">
                                    {["One personalized roadmap", "One story", "One Care Team Summary", "One continuous journey", "One place to return"].map((l) => (
                                        <li key={l} className="font-medium">✓ {l}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* === Why families stay — outcomes, not features === */}
                    <div className="pt-2 border-t border-cream-dark" data-testid="about-why-stay">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-1">Outcomes</p>
                        <h3 className="font-heading text-2xl md:text-3xl text-[#12776F]">
                            Why families stay with Memory Lane
                        </h3>
                        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-sm">
                            {[
                                ["Remembers their unique story", "No starting over at every appointment."],
                                ["Keeps everything organized", "Meds, behaviors, documents — one place."],
                                ["Prepares for appointments", "Walks in with the right questions."],
                                ["Stage-aware roadmap", "Calm guidance for what comes next."],
                                ["Trusted connections", "GUIDE, home health, hospice, respite."],
                                ["Between-visit support", "Lane is available 24/7."],
                                ["Easy provider transitions", "One Continuity Passport — share once."],
                                ["Day or night", "Quiet hours or hard hours."],
                            ].map(([title, body]) => (
                                <li key={title} className="bg-white border border-cream-dark rounded-2xl px-3 py-2">
                                    <p className="font-medium text-[#1F2624] leading-snug">{title}</p>
                                    <p className="text-xs text-[#4C5551] leading-snug mt-0.5">{body}</p>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* === When caregivers thrive === */}
                    <div className="pt-2 border-t border-cream-dark" data-testid="about-caregiver-wellbeing">
                        <p className="text-xs uppercase tracking-wider font-semibold text-coral-dark mb-1">Why caregiver support matters</p>
                        <h3 className="font-heading text-2xl md:text-3xl text-[#12776F]">
                            When caregivers thrive, patients benefit.
                        </h3>
                        <p className="mt-3 text-[15px] text-[#4C5551] leading-relaxed">
                            As caregiver stress increases, appointments become harder, medication mistakes
                            multiply, and crises rise. Memory Lane exists to support both the caregiver
                            and their loved one — from the first signs of memory change through every
                            stage of care — with personalized guidance, education, organization, and
                            continuous support.
                        </p>
                    </div>

                    {/* === Testimonials === */}
                    <div className="pt-2 border-t border-cream-dark" data-testid="about-testimonials">
                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-1">In their own words</p>
                        <h3 className="font-heading text-2xl md:text-3xl text-[#12776F]">
                            What changes for families on Memory Lane
                        </h3>
                        <div className="mt-3 grid md:grid-cols-3 gap-3">
                            {[
                                { quote: "Memory Lane became the one place I returned whenever I didn't know what to do next.", tag: "Adult-child caregiver · Texas" },
                                { quote: "For the first time since Mom's diagnosis, I felt like someone understood our whole story.", tag: "Spouse caregiver · Ohio" },
                                { quote: "I stopped feeling like I had to remember everything.", tag: "Long-distance caregiver · California" },
                            ].map((t, i) => (
                                <figure key={i} className="bg-white border border-cream-dark rounded-2xl p-4">
                                    <blockquote className="font-heading text-[15px] text-[#1F2624] leading-snug">&ldquo;{t.quote}&rdquo;</blockquote>
                                    <figcaption className="text-xs text-[#6E7A74] mt-2">{t.tag}</figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>

                    {/* Founder block */}
                    <div className="grid md:grid-cols-12 gap-6 md:gap-8 pt-2 border-t border-cream-dark" data-testid="landing-about-founder">
                        <div className="md:col-span-4">
                            <div className="aspect-[4/5] w-full max-w-[220px] mx-auto md:mx-0 rounded-2xl overflow-hidden bg-cream-dark shadow">
                                <img
                                    src="https://customer-assets.emergentagent.com/job_memory-support-guide/artifacts/vqmtf97v_Screenshot%202026-03-08%20at%208.23.50%E2%80%AFPM.png"
                                    alt="Portrait of Ashlee Skabla Velez, APRN, founder of Memory Lane Care"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                            <div className="mt-3 text-center md:text-left">
                                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Founder · NP</p>
                                <p className="font-heading text-lg text-[#1F2624] mt-0.5">Ashlee Skabla Velez, APRN</p>
                                <p className="text-xs text-[#4C5551]">TX · WA · FL — Texas NP Legislative Ambassador</p>
                            </div>
                        </div>
                        <div className="md:col-span-8 text-[#1F2624] leading-relaxed space-y-3 text-[15px]">
                            <p>
                                I&apos;m a Nurse Practitioner. I&apos;ve walked alongside families through
                                neurology, hospice, palliative care, and Medicare&apos;s GUIDE program.
                            </p>
                            <p>
                                The person I worried about most was rarely the patient. It was the
                                caregiver — figuring it out alone, overnight, with no map.
                            </p>
                            <p className="font-heading text-teal-dark text-lg">
                                That&apos;s why I built Memory Lane Care.
                            </p>
                            <p className="italic text-[#4C5551] text-sm">Because caregivers need care too.</p>
                        </div>
                    </div>
                </article>
            )}

            {tab === "how" && (
                <article
                    role="tabpanel"
                    data-testid="landing-panel-how"
                    className="ml-card p-6 md:p-8 bg-white animate-fade-in-up"
                >
                    <h3 className="font-heading text-2xl md:text-3xl text-[#12776F]">
                        Four steps. One roadmap.
                    </h3>
                    <ol className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                            { n: 1, label: "Tell us where you are", desc: "A 60-second intake — stage, living situation, what's hardest right now." },
                            { n: 2, label: "Get your roadmap", desc: "Personalized next steps for your loved one's stage — clinical + practical." },
                            { n: 3, label: "Talk to Lane (or Ashlee)", desc: "A patient AI companion any hour, plus optional 1:1s with a real NP." },
                            { n: 4, label: "Walk it together", desc: "Behavior guidance, hard conversations, resources — at the pace you can handle." },
                        ].map((s) => (
                            <li key={s.n} className="flex items-start gap-3 bg-cream-light rounded-2xl p-4">
                                <span className="w-9 h-9 rounded-2xl bg-teal text-white font-heading font-semibold flex items-center justify-center shrink-0">
                                    {s.n}
                                </span>
                                <div>
                                    <p className="font-heading font-medium text-[#1F2624]">{s.label}</p>
                                    <p className="text-sm text-[#4C5551] leading-relaxed">{s.desc}</p>
                                </div>
                            </li>
                        ))}
                    </ol>
                    <p className="mt-5 text-sm text-[#4C5551] italic">
                        Sign in and the in-app tour walks you through the rest.
                    </p>
                </article>
            )}

            {tab === "book" && (
                <article
                    role="tabpanel"
                    data-testid="landing-panel-book"
                    className="ml-card p-6 md:p-8 bg-white animate-fade-in-up"
                >
                    <div className="flex items-start gap-3 mb-4">
                        <img
                            src="https://customer-assets.emergentagent.com/job_memory-support-guide/artifacts/vqmtf97v_Screenshot%202026-03-08%20at%208.23.50%E2%80%AFPM.png"
                            alt="Portrait of Ashlee Skabla Velez, APRN"
                            className="w-14 h-14 rounded-2xl object-cover shrink-0"
                            loading="lazy"
                        />
                        <div className="flex-1">
                            <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                                Free 15-min walk-through
                            </p>
                            <h3 className="font-heading text-2xl md:text-3xl text-[#12776F] leading-tight mt-0.5">
                                Have Ashlee walk you through Memory Lane.
                            </h3>
                            <p className="mt-1.5 text-sm text-[#4C5551]">
                                No payment, no pressure. Pick a slot below and we&apos;ll meet on video.
                            </p>
                        </div>
                    </div>
                    <CalendlyInlineEmbed
                        testid="landing-calendly-inline"
                        url="https://calendly.com/ashlee-asvmedicalservices/new-meeting"
                    />
                </article>
            )}
        </section>
    );
}

export function RoadmapMilestones() {
    const [open, setOpen] = React.useState(null);
    const ref = React.useRef(null);

    // Close popover on outside click
    React.useEffect(() => {
        const onDoc = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(null);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const MILESTONES = [
        {
            label: "Diagnosis",
            desc: "Make sense of it",
            popover: "Plain-English explainers, the FAST scale, your first three next steps — plus second-opinion clinics and genetic-counseling resources.",
        },
        {
            label: "Early stage",
            desc: "Plan ahead",
            popover: "Power of attorney, advance directives, the driving conversation — plus support groups and memory clinics near you.",
        },
        {
            label: "Hard talks",
            desc: "Driving, finances",
            popover: "Gentle scripts written by a Nurse Practitioner for the hardest conversations — plus legal and financial planning resources.",
        },
        {
            label: "Day-to-day",
            desc: "Behaviors + care",
            popover: "Behavior guidance, medication tracking, burnout check-ins — plus respite care and home-health resources for your state or country.",
        },
        {
            label: "Late stage",
            desc: "Comfort + grace",
            popover: "Compassionate guidance through comfort care and grief — plus hospice and palliative-care directories.",
        },
    ];

    return (
        <ol
            ref={ref}
            className="grid grid-cols-5 gap-2 -mt-8 md:-mt-10"
            data-testid="roadmap-milestones"
        >
            {MILESTONES.map((m, i) => (
                <li key={i} className="relative flex flex-col items-center text-center">
                    <button
                        type="button"
                        data-testid={`milestone-${i}`}
                        aria-expanded={open === i}
                        onClick={() => setOpen(open === i ? null : i)}
                        className="flex flex-col items-center group focus:outline-none"
                    >
                        <span className={`w-5 h-5 rounded-full bg-white border-2 flex items-center justify-center mb-2 shadow-sm transition-colors ${
                            open === i ? "border-teal-dark ring-4 ring-teal-soft/60" : "border-teal group-hover:border-teal-dark"
                        }`}>
                            <span className="w-2 h-2 rounded-full bg-teal" />
                        </span>
                        <p className="font-heading font-medium text-[#1F2624] text-xs sm:text-sm group-hover:text-teal-dark transition-colors">{m.label}</p>
                        <p className="text-[10px] sm:text-xs text-[#4C5551] mt-0.5 leading-tight">{m.desc}</p>
                    </button>

                    {open === i && (
                        <div
                            role="dialog"
                            data-testid={`milestone-popover-${i}`}
                            className="absolute z-20 bottom-full mb-3 w-64 sm:w-72 -translate-x-1/2 left-1/2 ml-card p-3.5 bg-white shadow-xl border border-teal-soft text-left animate-fade-in-up"
                        >
                            <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-1">{m.label}</p>
                            <p className="text-sm text-[#1F2624] leading-relaxed">{m.popover}</p>
                            {/* Caret */}
                            <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-white border-r border-b border-teal-soft" />
                        </div>
                    )}
                </li>
            ))}
        </ol>
    );
}

function RotatingQuoteCard() {
    const { t } = useCountry();
    // Real caregiver quotes from the beta-tester feedback DB. Picked
    // randomly per page load so first-time visitors see a fresh one each
    // visit — keeps the page feeling alive without needing a CMS.
    const QUOTES = React.useMemo(() => ([
        {
            text: "Walking this with my mom is the hardest thing I've done. Memory Lane gave us a path.",
            attribution: "— Beta tester, Houston",
        },
        {
            text: "I called Ashlee crying at 9 pm. She didn't try to fix me — she just listened. I haven't felt that in two years.",
            attribution: "— Caregiver, daughter of a dad with Alzheimer's",
        },
        {
            text: "I was Googling 'how to tell my dad he can't drive' at 2 am. Memory Lane had the exact script — I cried because it sounded like a friend wrote it.",
            attribution: "— Caregiver, Seattle",
        },
    ]), []);
    const [q] = React.useState(() => QUOTES[Math.floor(Math.random() * QUOTES.length)]);

    return (
        <div
            data-testid="hero-rotating-quote"
            // Decorative — not interactive. Explicit cursor-default + role="figure"
            // so Clarity stops counting clicks here as "dead clicks".
            role="figure"
            aria-label="A caregiver quote from the beta program"
            className="hidden md:block absolute -bottom-6 -left-6 ml-card p-4 max-w-[280px] bg-white shadow-md cursor-default select-text"
        >
            <p className="text-sm text-[#1F2624] leading-relaxed">&ldquo;{t(q.text)}&rdquo;</p>
            <p className="text-xs text-[#4C5551] mt-2">{t(q.attribution)}</p>
        </div>
    );
}

/* Compact country picker — flag + 2-letter code in a chip-style select.
 * Shared between the landing hero and the /login page so caregivers from
 * anywhere on Earth can flag their country before they sign up or sign in.
 * Stores via useCountry(): localStorage + (when authed) /me/country-language.
 */
export function CountryPickerCompact({ value, onChange, testid = "country-picker" }) {
    return (
        <label className="relative inline-flex items-center" data-testid={`${testid}-wrap`}>
            <span className="sr-only">Country</span>
            <select
                value={(value || "US").toUpperCase()}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className="ml-input !min-h-[56px] !pl-3 !pr-8 !py-0 !text-base appearance-none bg-white cursor-pointer font-medium tracking-wide"
                aria-label="Select your country"
            >
                {SUPPORTED_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                        {c.flag}  {c.code}
                    </option>
                ))}
            </select>
            <ChevronDown size={14} strokeWidth={2} className="absolute right-2.5 text-[#4C5551] pointer-events-none" />
        </label>
    );
}

/* Country-aware welcome strip — when the picker is on a non-US country
 * with curated local resources, surface a one-liner + 2 quick local
 * resource chips so international caregivers immediately see this isn't
 * a US-only product. Silent for US (default) to avoid noise. */
function CountryWelcomeStrip() {
    const { country } = useCountry();
    const cc = (country || "US").toUpperCase();
    if (cc === "US" || !INTL_COUNTRIES_WITH_RESOURCES.includes(cc)) return null;
    const meta = countryMeta(cc);
    const local = getInternationalResources(cc).slice(0, 2);
    if (!local.length) return null;
    return (
        <div
            data-testid="country-welcome-strip"
            className="mt-4 max-w-xl rounded-2xl bg-cream-light border border-cream-dark px-4 py-3 animate-fade-in-up"
        >
            <p className="text-sm text-[#1F2624] leading-snug">
                <span aria-hidden="true" className="mr-1.5">{meta.flag}</span>
                <span className="font-medium">You&apos;re in {meta.label}</span> — Memory Lane already has resources from{" "}
                {local.map((r, i) => (
                    <React.Fragment key={r.id}>
                        {i > 0 && (i === local.length - 1 ? " and " : ", ")}
                        <a
                            href={r.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`country-welcome-link-${r.id}`}
                            className="text-teal-dark underline hover:text-teal whitespace-nowrap"
                        >
                            {r.name}
                        </a>
                    </React.Fragment>
                ))}
                .
            </p>
            <p className="text-xs text-[#6E7A74] mt-1">Your dashboard will surface more once you sign in.</p>
        </div>
    );
}

function HeroSignupForm() {
    const { refresh } = useAuth();
    const { country, setCountry, t } = useCountry();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [memoryConsent, setMemoryConsent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Consent-impression event — fire ONCE per session when the hero
    // form (which carries the checkbox) mounts.
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem("ml_ga_memory_consent_shown_hero")) return;
        sessionStorage.setItem("ml_ga_memory_consent_shown_hero", "1");
        track(EVENTS.MEMORY_CONSENT_SHOWN, { form: "hero_signup" });
    }, []);

    // Fire MEMORY_CONSENT_AGREED the first time the caregiver ticks
    // the box during this session.
    useEffect(() => {
        if (!memoryConsent) return;
        if (typeof window === "undefined") return;
        if (sessionStorage.getItem("ml_ga_memory_consent_agreed_hero")) return;
        sessionStorage.setItem("ml_ga_memory_consent_agreed_hero", "1");
        track(EVENTS.MEMORY_CONSENT_AGREED, { form: "hero_signup" });
    }, [memoryConsent]);

    const submit = async (e) => {
        e.preventDefault();
        const cleanEmail = email.trim().toLowerCase();
        if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
            toast.error("That email looks off.");
            return;
        }
        // Storage consent is required — the caregiver actively agrees
        // that Lane can hold onto their story. We persist the decision
        // + timestamp locally as an audit breadcrumb (MHMDA / GDPR).
        if (!memoryConsent) {
            toast.error("Tick the box so Lane can remember your story.");
            return;
        }
        // Funnel: a valid, consented email submission has begun.
        track(EVENTS.SIGNUP_STARTED, { form: "landing_hero" });
        try {
            localStorage.setItem("mlcn_data_consent_v1", JSON.stringify({
                decided: true, granted: true, ts: Date.now(), version: 1,
            }));
        } catch { /* private mode — non-blocking */ }
        setSubmitting(true);
        try {
            // Secure passwordless signup — one email carries a 6-digit code
            // and a one-tap sign-in link. The account (and session) is only
            // created after the caregiver verifies on /login.
            await api.post("/auth/passwordless/start", { email: cleanEmail, country });
            toast.success("Check your email — we sent a 6-digit code and a one-tap sign-in link.");
            navigate(`/login?email=${encodeURIComponent(cleanEmail)}`, { state: { codeSent: true } });
        } catch (err) {
            if (err?.response?.status === 429) {
                // A code is already in flight — send them to the code step.
                toast(err?.response?.data?.detail || "A code is already on its way — check your inbox.");
                navigate(`/login?email=${encodeURIComponent(cleanEmail)}`, { state: { codeSent: true } });
                return;
            }
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Couldn't send your code. Try again?");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form
            onSubmit={submit}
            data-testid="hero-signup-form"
            className="mt-7 max-w-xl"
        >
            <div className="flex flex-col sm:flex-row gap-2.5">
                {/* Country picker — sits inline with the email input so
                    caregivers from CA/GB/AU/etc. get the right resources
                    from their very first dashboard visit. */}
                <CountryPickerCompact
                    value={country}
                    onChange={setCountry}
                    testid="hero-country-picker"
                />
                <input
                    type="email"
                    data-testid="hero-email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("Your email")}
                    autoComplete="email"
                    className="ml-input flex-1 !text-base sm:!text-lg !min-h-[56px]"
                    required
                    aria-label="Email address"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    data-testid="hero-beta-cta"
                    className="ml-btn-primary !min-h-[56px] !px-7 disabled:opacity-50 whitespace-nowrap"
                >
                    {submitting
                        ? <><Loader2 size={16} className="animate-spin" /> {t("One sec…")}</>
                        : <>{t("Try Memory Lane")} <ArrowRight size={18} strokeWidth={1.8} /></>
                    }
                </button>
            </div>
            <p className="mt-2.5 text-xs text-[#6E7A74] leading-snug">
                {t("Free. 30 seconds. No password — we'll email you a magic link if you ever need to come back from a new device.")}
            </p>
            <label
                className="mt-3 flex items-start gap-2.5 cursor-pointer select-none"
                data-testid="hero-memory-consent-label"
            >
                <input
                    type="checkbox"
                    data-testid="hero-memory-consent"
                    checked={memoryConsent}
                    onChange={(e) => setMemoryConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-cream-dark text-teal-dark focus:ring-2 focus:ring-teal-soft"
                    required
                />
                <span className="text-sm text-[#4C5551] leading-snug">
                    {t("I'm happy for Memory Lane to store my caregiving notes so Lane can remember my story.")}
                </span>
            </label>
            {/* Three secondary entry paths, labelled clearly so caregivers
                and partners don't have to guess which door is theirs. All
                three live on /login (Welcome Back · Partner code · Org). */}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
                <Link to="/login" data-testid="hero-existing-signin" className="text-teal-dark underline hover:text-teal">
                    {t("Already have a Memory Lane account →")}
                </Link>
                <Link to="/login#partner-code" data-testid="hero-partner-code-signin" className="text-teal-dark underline hover:text-teal">
                    {t("I have a partner code →")}
                </Link>
                <Link to="/login#org" data-testid="hero-org-signin" className="text-teal-dark underline hover:text-teal">
                    {t("Organization sign in →")}
                </Link>
                <Link to="/terms" target="_blank" className="text-xs text-[#6E7A74] underline">{t("Terms")}</Link>
            </div>
        </form>
    );
}


function timeAgo(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

function LiveSignupTicker({ fallbackCount }) {
    const { t } = useCountry();
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        let alive = true;
        api.get("/recent-signups")
            .then(r => { if (alive) setItems(r.data?.items || []); })
            .catch(() => { });
        return () => { alive = false; };
    }, []);

    useEffect(() => {
        if (items.length < 2) return;
        const t = setInterval(() => setIdx(i => (i + 1) % items.length), 4000);
        return () => clearInterval(t);
    }, [items.length]);

    if (items.length === 0) {
        if (fallbackCount && fallbackCount > 0) {
            return (
                <p className="mt-5 text-sm text-[#4C5551]" data-testid="beta-count">
                    <span className="font-semibold text-teal-dark">{fallbackCount}</span>{" "}
                    {fallbackCount === 1 ? t("family") : t("families")} {t("already on the journey with us")}
                </p>
            );
        }
        return null;
    }

    const current = items[idx];
    return (
        <div
            className="mt-5 flex items-center gap-2.5 text-sm text-[#4C5551]"
            data-testid="live-signup-ticker"
            aria-live="polite"
        >
            <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full rounded-full bg-teal opacity-60 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal-dark"></span>
            </span>
            <span
                key={`${current.first_name}-${current.at}`}
                className="animate-fade-in-up"
                data-testid="live-signup-item"
            >
                <span className="font-semibold text-teal-dark">{current.first_name}</span> {t("just started their roadmap")}
                <span className="text-[#6E7A74]"> · {timeAgo(current.at)}</span>
            </span>
        </div>
    );
}


/* OrgCodeQuickEntry
 * --------------------
 * Lives on the landing-page hero. Family caregivers whose hospice / GUIDE
 * program / clinic gave them an 8-char Memory Lane invite code can paste it
 * here without leaving the landing page. We validate the code format, stash
 * it in sessionStorage so the dashboard's existing redeemer picks it up
 * automatically post-sign-in, then send them to /login or /beta.
 */
function OrgCodeQuickEntry() {
    const [code, setCode] = useState("");
    const navigate = useNavigate();
    const submit = (e) => {
        e?.preventDefault?.();
        const c = code.trim().toUpperCase();
        if (!/^[A-Z2-9]{8}$/.test(c)) {
            toast("Codes are 8 letters/numbers. Double-check yours.");
            return;
        }
        try { sessionStorage.setItem("mlcn_pending_org_code", c); } catch { /* ignore */ }
        toast.success("Saved — sign in or apply to finish connecting.");
        navigate("/login");
    };
    return (
        <details
            className="mt-4 w-full sm:max-w-md rounded-2xl border border-[#E2E8F0] bg-white"
            data-testid="hero-org-code"
        >
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-[#1F2624] hover:bg-cream-light rounded-2xl flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2">
                    <KeyRound size={15} strokeWidth={1.8} className="text-teal-dark" />
                    Have an invite code from your care provider?
                </span>
                <ChevronDown size={14} strokeWidth={1.8} className="text-[#6E7A74]" />
            </summary>
            <form onSubmit={submit} className="px-4 pb-4 pt-1 flex flex-wrap items-center gap-2">
                <input
                    data-testid="hero-org-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="e.g. ABC23XYZ"
                    maxLength={8}
                    className="flex-1 min-w-[160px] px-3 py-2.5 rounded-2xl bg-cream-light border border-[#E2E8F0] text-[#1F2624] font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-teal-soft text-sm"
                />
                <button
                    type="submit"
                    disabled={code.trim().length < 8}
                    data-testid="hero-org-code-submit"
                    className="px-4 py-2.5 rounded-2xl bg-teal-dark text-white text-sm font-medium disabled:opacity-60"
                >
                    Continue
                </button>
                <p className="w-full text-xs text-[#6E7A74] mt-1">
                    Already signed up?{" "}
                    <Link to="/login" data-testid="hero-org-code-signin" className="text-teal-dark underline">
                        Sign in
                    </Link>{" "}
                    and we&apos;ll connect your account automatically.
                </p>
            </form>
        </details>
    );
}


/* CareProviderInterestStrip
 * --------------------------
 * Simple, low-pressure lead capture for agencies / care providers /
 * GUIDE programs. One short row of fields (email + org + role) → backend
 * `partner_interest` collection. No pricing, no marketing pitch — the
 * deeper org-pilot story lives on /partners for those who want it.
 */
export function CareProviderInterestStrip() {
    const [email, setEmail] = useState("");
    const [orgName, setOrgName] = useState("");
    const [role, setRole] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const cleanEmail = email.trim().toLowerCase();
        if (!/^\S+@\S+\.\S+$/.test(cleanEmail)) {
            toast.error("That email looks off.");
            return;
        }
        if (!orgName.trim()) {
            toast.error("Please tell us your organization name.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/partners/interest", {
                email: cleanEmail,
                org_name: orgName.trim(),
                role: role.trim(),
            });
            track(EVENTS.PARTNER_REFERRAL, {
                source: "landing_provider_form",
                org_name: orgName.trim(),
                role: role.trim(),
            });
            setDone(true);
            toast.success("Thanks — Ashlee will reach out soon.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(detail || "Couldn't send right now. Try again?");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="for-providers"
            aria-label="For care providers and agencies"
            data-testid="landing-provider-interest"
            className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14"
        >
            <div className="ml-card p-6 md:p-8 bg-teal-soft/40 border border-teal-light">
                <div className="grid md:grid-cols-12 gap-6 items-start">
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="w-9 h-9 rounded-2xl bg-teal-dark text-white flex items-center justify-center shrink-0">
                                <Hospital size={16} strokeWidth={1.8} />
                            </div>
                            <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">For care providers</p>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-heading font-medium text-[#12776F] leading-snug">
                            Are you an agency or care provider?
                        </h2>
                        <p className="mt-2 text-sm md:text-base text-[#4C5551] leading-relaxed">
                            Tell us a bit about your team and we&apos;ll be in touch about supporting your families with Memory Lane.
                        </p>
                    </div>

                    <div className="md:col-span-7">
                        {done ? (
                            <div
                                data-testid="provider-interest-thanks"
                                className="rounded-2xl bg-white border border-teal-light p-5 text-sm text-[#1F2624]"
                            >
                                <p className="font-heading text-lg text-teal-dark">Thank you.</p>
                                <p className="mt-1 text-[#4C5551]">
                                    Ashlee will reach out personally — usually within a day or two.
                                </p>
                            </div>
                        ) : (
                            <form
                                onSubmit={submit}
                                data-testid="provider-interest-form"
                                className="grid grid-cols-1 sm:grid-cols-2 gap-2.5"
                            >
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Work email"
                                    autoComplete="email"
                                    required
                                    aria-label="Work email"
                                    data-testid="provider-interest-email"
                                    className="ml-input !min-h-[48px] sm:col-span-2"
                                />
                                <input
                                    type="text"
                                    value={orgName}
                                    onChange={(e) => setOrgName(e.target.value)}
                                    placeholder="Organization (e.g. Sunrise Hospice)"
                                    required
                                    aria-label="Organization name"
                                    data-testid="provider-interest-org"
                                    className="ml-input !min-h-[48px]"
                                />
                                <input
                                    type="text"
                                    value={role}
                                    onChange={(e) => setRole(e.target.value)}
                                    placeholder="Your role (e.g. Clinical Director)"
                                    aria-label="Your role"
                                    data-testid="provider-interest-role"
                                    className="ml-input !min-h-[48px]"
                                />
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="provider-interest-submit"
                                    className="ml-btn-primary !min-h-[48px] sm:col-span-2 disabled:opacity-60"
                                >
                                    {submitting
                                        ? <><Loader2 size={16} className="animate-spin" /> Sending…</>
                                        : <>Tell Ashlee I&apos;m interested <ArrowRight size={16} strokeWidth={1.8} /></>
                                    }
                                </button>
                                <p className="text-xs text-[#6E7A74] sm:col-span-2">
                                    No pitch, no spam. We&apos;ll just send a short reply with how Memory Lane fits your team.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}


// ============================================================================
// FourFeatureCards — 10-second "what Memory Lane does" scan
// ============================================================================
export function FourFeatureCards() {
    const { t } = useCountry();
    const cards = [
        {
            icon: Compass,
            titleKey: "landing.cards.roadmap.title",
            blurbKey: "landing.cards.roadmap.blurb",
            to: "/roadmap",
            testid: "feature-card-roadmap",
            tone: "stage_early",
        },
        {
            icon: MessageCircle,
            titleKey: "landing.cards.lane.title",
            blurbKey: "landing.cards.lane.blurb",
            to: "/chat",
            testid: "feature-card-lane",
            tone: "teal",
        },
        {
            icon: ClipboardList,
            titleKey: "landing.cards.summary.title",
            blurbKey: "landing.cards.summary.blurb",
            to: "/care-summary",
            testid: "feature-card-summary",
            tone: "ml_blue",
        },
        {
            icon: MapPin,
            titleKey: "landing.cards.resources.title",
            blurbKey: "landing.cards.resources.blurb",
            to: "/help-finder",
            testid: "feature-card-resources",
            tone: "stage_middle",
        },
    ];
    const TONE = {
        stage_early: "from-stage_early/20 text-stage_early-dark",
        teal: "from-teal-soft text-teal-dark",
        ml_blue: "from-ml_blue-light text-ml_blue-dark",
        stage_middle: "from-stage_middle/20 text-stage_middle-dark",
    };
    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14"
            data-testid="landing-feature-cards"
            aria-label="What Memory Lane does"
        >
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {cards.map((c) => {
                    const Icon = c.icon;
                    return (
                        <Link
                            key={c.titleKey}
                            to={c.to}
                            data-testid={c.testid}
                            className={`ml-card p-5 md:p-6 bg-gradient-to-br ${TONE[c.tone]} to-white hover:shadow-md transition-shadow group block`}
                        >
                            <span className={`inline-flex w-10 h-10 items-center justify-center rounded-2xl bg-white shadow-sm`}>
                                <Icon size={20} strokeWidth={1.8} />
                            </span>
                            <h3 className="font-heading text-lg md:text-xl text-[#1F2624] mt-3 leading-snug">
                                {t(c.titleKey)}
                            </h3>
                            <p className="text-sm text-[#4C5551] mt-1 leading-relaxed">
                                {t(c.blurbKey)}
                            </p>
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-dark mt-3 group-hover:translate-x-0.5 transition-transform">
                                {t("landing.cards.explore")} <ChevronRight size={12} strokeWidth={2} />
                            </span>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
}

// ============================================================================
// JourneyOverlay — the moat. Side-by-side: what exists today vs Memory Lane.
// ============================================================================
export function JourneyOverlay() {
    const { t } = useCountry();
    const stages = [
        // Pre-diagnosis entry points — Memory Lane begins HERE, not at diagnosis.
        t("landing.journey.stage.noticing"),
        t("landing.journey.stage.evaluation"),
        t("landing.journey.stage.diagnosis"),
        t("landing.journey.stage.early"),
        t("landing.journey.stage.middle"),
        t("landing.journey.stage.late"),
        t("landing.journey.stage.burnout"),
        t("landing.journey.stage.hospice"),
    ];
    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-16"
            data-testid="landing-journey-overlay"
            aria-label="The memory care continuum — from first signs through advanced care"
        >
            <div className="ml-card overflow-hidden bg-white">
                <div className="p-6 md:p-10 border-b border-cream-dark/60 text-center">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                        {t("landing.journey.eyebrow")}
                    </p>
                    <h2 className="font-heading text-3xl md:text-4xl text-[#12776F] mt-2 leading-tight max-w-2xl mx-auto">
                        {t("landing.journey.title")}
                    </h2>
                    <p className="text-sm md:text-base text-[#4C5551] mt-3 max-w-xl mx-auto leading-relaxed">
                        {t("landing.journey.intro")}
                    </p>
                </div>
                <div className="grid md:grid-cols-2 gap-0">
                    {/* Left: Fragmented */}
                    <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-cream-dark/60" data-testid="journey-fragmented">
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-coral-dark mb-4">
                            {t("landing.journey.todayLabel")}
                        </p>
                        <ol className="space-y-2.5">
                            {stages.map((s) => (
                                <li
                                    key={s}
                                    // Decorative diagram — explicit cursor-default
                                    // so Clarity doesn't flag the rounded-xl card
                                    // styling as inviting clicks.
                                    className="flex items-center gap-3 p-3 rounded-xl bg-cream-light cursor-default"
                                >
                                    <span className="w-2 h-2 rounded-full bg-coral-dark/70 shrink-0" />
                                    <span className="text-[15px] text-[#1F2624]">{s}</span>
                                    <span className="ml-auto text-[11px] uppercase tracking-wider text-[#6E7A74]">{t("landing.journey.handedOff")}</span>
                                </li>
                            ))}
                        </ol>
                        <p className="text-xs text-[#6E7A74] mt-4 italic">
                            {t("landing.journey.todayCaption")}
                        </p>
                    </div>
                    {/* Right: Memory Lane spans */}
                    <div className="p-6 md:p-8 relative bg-gradient-to-br from-teal-soft/60 to-cream-light" data-testid="journey-continuous">
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-teal-dark mb-4">
                            {t("landing.journey.withMlLabel")}
                        </p>
                        {/* Continuous spine */}
                        <div className="relative">
                            <span aria-hidden="true" className="absolute left-1.5 top-3 bottom-3 w-1 bg-teal rounded-full" />
                            <ol className="space-y-2.5 relative">
                                {stages.map((s) => (
                                    <li
                                        key={s}
                                        className="flex items-center gap-3 p-3 pl-7 rounded-xl bg-white border border-cream-dark cursor-default"
                                    >
                                        <span className="absolute left-0 w-4 h-4 rounded-full bg-teal border-4 border-white shrink-0" />
                                        <span className="text-[15px] text-[#1F2624]">{s}</span>
                                        <Check size={14} strokeWidth={2.4} className="ml-auto text-teal-dark shrink-0" />
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <p className="text-xs text-teal-dark mt-4 font-semibold">
                            {t("landing.journey.withMlCaption")}
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ============================================================================
// LandingSearchSection — promote universal search to its own visible section.
// ============================================================================
export function LandingSearchSection() {
    const { t } = useCountry();
    return (
        <section
            className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14"
            data-testid="landing-search-section"
        >
            <div className="text-center mb-5">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                    {t("landing.search.eyebrow")}
                </p>
                <h2 className="font-heading text-2xl md:text-3xl text-[#12776F] mt-1 leading-tight">
                    {t("landing.search.title")}
                </h2>
                <p className="text-sm text-[#4C5551] mt-2 max-w-xl mx-auto">
                    {t("landing.search.sub")}
                </p>
            </div>
            <SearchMemoryLane testid="landing-search" />
        </section>
    );
}

// ============================================================================
// MeetLane — show a real conversation, not "AI Chat".
// Rotates through 4 real caregiver moments every 6s so the demo reel
// proves Lane handles the full emotional range of dementia caregiving.
// ============================================================================
const LANE_CONVERSATIONS = [
    {
        time: "2:14 pm",
        askKey: "landing.lane.conv1.ask",
        whyKey: "landing.lane.conv1.why",
        leadInKey: "landing.lane.conv1.leadIn",
        stepKeys: ["landing.lane.conv1.s1", "landing.lane.conv1.s2", "landing.lane.conv1.s3"],
        followupKey: "landing.lane.conv1.followup",
    },
    {
        time: "11:48 pm",
        askKey: "landing.lane.conv2.ask",
        whyKey: "landing.lane.conv2.why",
        leadInKey: "landing.lane.conv2.leadIn",
        stepKeys: ["landing.lane.conv2.s1", "landing.lane.conv2.s2", "landing.lane.conv2.s3"],
        followupKey: "landing.lane.conv2.followup",
    },
    {
        time: "6:02 am",
        askKey: "landing.lane.conv3.ask",
        whyKey: "landing.lane.conv3.why",
        leadInKey: "landing.lane.conv3.leadIn",
        stepKeys: ["landing.lane.conv3.s1", "landing.lane.conv3.s2", "landing.lane.conv3.s3"],
        followupKey: "landing.lane.conv3.followup",
    },
    {
        time: "9:31 pm",
        askKey: "landing.lane.conv4.ask",
        whyKey: "landing.lane.conv4.why",
        leadInKey: "landing.lane.conv4.leadIn",
        stepKeys: ["landing.lane.conv4.s1", "landing.lane.conv4.s2", "landing.lane.conv4.s3"],
        followupKey: "landing.lane.conv4.followup",
    },
];

export function MeetLane() {
    const { t } = useCountry();
    const [idx, setIdx] = React.useState(0);
    React.useEffect(() => {
        const tm = setInterval(() => setIdx((i) => (i + 1) % LANE_CONVERSATIONS.length), 7000);
        return () => clearInterval(tm);
    }, []);
    const c = LANE_CONVERSATIONS[idx];
    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
            data-testid="landing-meet-lane"
        >
            <div className="grid md:grid-cols-2 gap-10 items-center">
                <div>
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                        {t("landing.lane.eyebrow")}
                    </p>
                    <h2 className="font-heading text-3xl md:text-4xl text-[#12776F] mt-2 leading-tight">
                        {t("landing.lane.title")}
                    </h2>
                    <p className="text-base text-[#4C5551] mt-4 leading-relaxed">
                        {t("landing.lane.body")}
                    </p>
                    <ul className="mt-5 space-y-2 text-sm text-[#1F2624]">
                        <li className="flex items-start gap-2"><Check size={16} strokeWidth={2.2} className="text-teal-dark mt-0.5 shrink-0" /> {t("landing.lane.bullet1")}</li>
                        <li className="flex items-start gap-2"><Check size={16} strokeWidth={2.2} className="text-teal-dark mt-0.5 shrink-0" /> {t("landing.lane.bullet2")}</li>
                        <li className="flex items-start gap-2"><Check size={16} strokeWidth={2.2} className="text-teal-dark mt-0.5 shrink-0" /> {t("landing.lane.bullet3")}</li>
                    </ul>
                    <Link
                        to="/login"
                        data-testid="meet-lane-cta"
                        className="ml-btn-primary !min-h-[48px] mt-6 inline-flex items-center gap-2"
                    >
                        {t("landing.lane.cta")} <ArrowRight size={16} strokeWidth={1.8} />
                    </Link>

                    {/* Dots — let caregivers see this rotates and which conversation is on screen */}
                    <div className="flex items-center gap-2 mt-6" data-testid="lane-conv-dots">
                        {LANE_CONVERSATIONS.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setIdx(i)}
                                aria-label={`Show conversation ${i + 1}`}
                                data-testid={`lane-conv-dot-${i}`}
                                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-teal" : "w-1.5 bg-cream-dark hover:bg-teal-soft"}`}
                            />
                        ))}
                    </div>
                </div>

                {/* Conversation card — keys on idx so React replays the
                    fade-in animation when the rotation advances. */}
                <div
                    key={idx}
                    className="ml-card p-5 md:p-6 bg-gradient-to-br from-cream-light to-white animate-fade-in-up"
                    data-testid="lane-conversation-demo"
                >
                    <div className="flex items-center gap-2 mb-4 text-xs text-[#6E7A74]">
                        <span className="w-2 h-2 rounded-full bg-stage_early-dark" />
                        <span className="font-semibold">{t("landing.lane.liveTag")}</span>
                        <span className="ml-auto" data-testid="lane-conv-time">{c.time}</span>
                    </div>
                    {/* Caregiver msg */}
                    <div className="flex justify-end mb-3">
                        <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-teal text-white px-4 py-2.5 shadow-sm" data-testid="lane-conv-ask">
                            <p className="text-[14px] leading-relaxed">{t(c.askKey)}</p>
                        </div>
                    </div>
                    {/* Lane msg */}
                    <div className="flex justify-start mb-3">
                        <div className="max-w-[92%] rounded-2xl rounded-tl-md bg-white border border-cream-dark px-4 py-3 shadow-sm">
                            <p className="text-[14px] leading-relaxed text-[#1F2624]">{t(c.whyKey)}</p>
                            <p className="text-[14px] leading-relaxed text-[#1F2624] mt-2">{t(c.leadInKey)}</p>
                            <ul className="text-[13px] text-[#4C5551] mt-1.5 space-y-1 list-disc list-inside">
                                {c.stepKeys.map((sk, i) => <li key={i}>{t(sk)}</li>)}
                            </ul>
                            <p className="text-[12px] text-teal-dark mt-2 italic">{t(c.followupKey)}</p>
                        </div>
                    </div>
                    <p className="text-[11px] text-center text-[#6E7A74] mt-3">
                        {t("landing.lane.footnote")}
                    </p>
                </div>
            </div>
        </section>
    );
}

// ============================================================================
// OrganizationsBand — Memory Lane is the FRONT DOOR. Families come here
// FIRST (often without a diagnosis), and we route them outward to every
// service they'll touch across the journey.
// ============================================================================
export function OrganizationsBand() {
    const { t } = useCountry();
    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
            data-testid="landing-organizations"
        >
            <div className="text-center mb-8">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                    {t("landing.orgs.eyebrow")}
                </p>
                <h2 className="font-heading text-3xl md:text-4xl text-[#12776F] mt-2 leading-tight max-w-2xl mx-auto">
                    {t("landing.orgs.title")}
                </h2>
                <p className="text-sm md:text-base text-[#4C5551] mt-3 max-w-xl mx-auto leading-relaxed">
                    {t("landing.orgs.intro")}
                </p>
            </div>

            <div className="ml-card p-6 md:p-10 bg-white" data-testid="connective-tissue">
                <FrontDoorFlow />

                <p className="text-center text-sm text-[#4C5551] mt-8 max-w-2xl mx-auto leading-relaxed">
                    {t("landing.orgs.closing")}
                </p>
                <div className="flex justify-center mt-5">
                    <Link
                        to="/partners"
                        data-testid="org-partners-cta"
                        className="ml-btn-secondary inline-flex items-center gap-2"
                    >
                        {t("landing.orgs.cta")} <ArrowRight size={16} strokeWidth={1.8} />
                    </Link>
                </div>
            </div>
        </section>
    );
}

// Vertical flow diagram for the front-door story.
//   Concerned Family
//        │
//    Memory Lane    ← front door (highlighted)
//      ↙     ↘
// Primary Care   Neurology
//          ↓
//        GUIDE
//          ↓
//     Home Health
//          ↓
//       Hospice
//          ↓
//   Community Support
const FlowNode = ({ icon: Icon, label, sub, accent, testid, size = "md" }) => (
    <div
        data-testid={testid}
        className={`flex flex-col items-center justify-center text-center rounded-2xl border ${size === "lg" ? "px-6 py-4 min-w-[160px]" : "px-4 py-3 min-w-[120px]"} ${accent
            ? "bg-teal text-white border-teal-dark/40 shadow-md ring-2 ring-teal-dark/20"
            : "bg-cream-light text-[#1F2624] border-cream-dark"}`}
    >
        <Icon size={size === "lg" ? 22 : 18} strokeWidth={1.8} />
        <span className={`text-[12px] md:text-sm font-semibold mt-1.5 ${accent ? "text-white" : ""}`}>{label}</span>
        {sub && <span className={`text-[10px] mt-0.5 ${accent ? "text-teal-soft" : "text-[#6E7A74]"}`}>{sub}</span>}
    </div>
);

const FlowDown = ({ small }) => (
    <div className="flex justify-center" aria-hidden="true">
        <ChevronDown size={small ? 18 : 22} strokeWidth={1.8} className="text-teal-dark/60" />
    </div>
);

export function FrontDoorFlow() {
    const { t } = useCountry();
    return (
        <div className="flex flex-col items-center gap-2.5 md:gap-3 max-w-2xl mx-auto" data-testid="front-door-flow">
            {/* Concerned family — entry point */}
            <FlowNode icon={Users} label={t("landing.orgs.node.family")} sub={t("landing.orgs.node.familySub")} testid="org-node-family" />
            <FlowDown />

            {/* Memory Lane — the front door, highlighted */}
            <FlowNode icon={Compass} label={t("landing.orgs.node.memorylane")} sub={t("landing.orgs.node.memorylaneSub")} accent testid="org-node-memorylane" size="lg" />

            {/* Branch into Primary Care + Neurology */}
            <div className="relative w-full max-w-md py-1" aria-hidden="true">
                <svg viewBox="0 0 200 36" className="w-full h-7 md:h-8" preserveAspectRatio="none">
                    {/* Trunk down from Memory Lane */}
                    <line x1="100" y1="0" x2="100" y2="14" stroke="#1D8A85" strokeWidth="1.6" strokeOpacity="0.6" />
                    {/* Horizontal beam */}
                    <line x1="32" y1="14" x2="168" y2="14" stroke="#1D8A85" strokeWidth="1.6" strokeOpacity="0.6" />
                    {/* Down legs */}
                    <line x1="32" y1="14" x2="32" y2="34" stroke="#1D8A85" strokeWidth="1.6" strokeOpacity="0.6" />
                    <line x1="168" y1="14" x2="168" y2="34" stroke="#1D8A85" strokeWidth="1.6" strokeOpacity="0.6" />
                </svg>
            </div>
            <div className="grid grid-cols-2 gap-3 md:gap-6 w-full max-w-md" data-testid="org-branch-diagnosis">
                <div className="flex justify-center">
                    <FlowNode icon={Stethoscope} label={t("landing.orgs.node.primary")} testid="org-node-primary-care" />
                </div>
                <div className="flex justify-center">
                    <FlowNode icon={Brain} label={t("landing.orgs.node.neurology")} testid="org-node-neurology" />
                </div>
            </div>

            <FlowDown />
            <FlowNode icon={ClipboardList} label={t("landing.orgs.node.guide")} testid="org-node-guide" />
            <FlowDown />
            <FlowNode icon={HomeIcon} label={t("landing.orgs.node.homeHealth")} testid="org-node-home-health" />
            <FlowDown />
            <FlowNode icon={Hospital} label={t("landing.orgs.node.hospice")} testid="org-node-hospice" />
            <FlowDown />
            <FlowNode icon={HandHeart} label={t("landing.orgs.node.community")} testid="org-node-community" />
        </div>
    );
}

// ============================================================================
// ImpactNumbers — caregiver-impact stats, not vanity.
// ============================================================================
export function ImpactNumbers() {
    const { t } = useCountry();
    // Numbers are pulled from /api/landing/impact-stats when available;
    // when the backend hasn't been seeded yet (or the request fails),
    // we fall back to ranges that read as honest, conservative founder-set
    // milestones rather than fabricated metrics.
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/landing/impact-stats").then((r) => setStats(r.data)).catch(() => { });
    }, []);
    const items = [
        { labelKey: "landing.impact.familiesSupported", key: "families_supported", fallbackKey: "landing.impact.fallbackGrowing" },
        { labelKey: "landing.impact.questionsAnswered", key: "questions_answered", fallbackKey: "landing.impact.fallbackDaily" },
        { labelKey: "landing.impact.appointmentsPrepared", key: "appointments_prepared", fallbackKey: "landing.impact.fallbackWeekly" },
        { labelKey: "landing.impact.resourcesConnected", key: "resources_connected", fallback: "1,000+" },
        { labelKey: "landing.impact.navigatorSessions", key: "navigator_sessions", fallbackKey: "landing.impact.fallbackBookedWeekly" },
    ];
    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14"
            data-testid="landing-impact-numbers"
        >
            <div className="text-center mb-6">
                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                    {t("landing.impact.eyebrow")}
                </p>
                <h2 className="font-heading text-2xl md:text-3xl text-[#12776F] mt-2 leading-tight">
                    {t("landing.impact.title")}
                </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {items.map((it) => (
                    <div
                        key={it.key}
                        className="ml-card p-4 text-center bg-white"
                        data-testid={`impact-stat-${it.key}`}
                    >
                        <div className="font-heading text-2xl md:text-3xl text-teal-dark tabular-nums">
                            {stats && stats[it.key] != null ? stats[it.key] : (it.fallbackKey ? t(it.fallbackKey) : it.fallback)}
                        </div>
                        <div className="text-xs text-[#4C5551] mt-1.5 leading-tight">{t(it.labelKey)}</div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// ============================================================================
// FinalCTA — three concrete paths forward.
// ============================================================================
export function FinalCTA() {
    const { t } = useCountry();
    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20"
            data-testid="landing-final-cta"
        >
            <div className="ml-card p-8 md:p-12 bg-gradient-to-br from-teal to-teal-dark text-white text-center">
                <h2 className="font-heading text-3xl md:text-4xl leading-tight max-w-2xl mx-auto">
                    {t("landing.cta.title")}
                </h2>
                <p className="text-base md:text-lg text-teal-soft mt-4 max-w-xl mx-auto leading-relaxed">
                    {t("landing.cta.body")}
                </p>
                <div className="mt-7 flex flex-wrap justify-center gap-3" data-testid="final-cta-buttons">
                    <Link
                        to="/login"
                        data-testid="final-cta-start"
                        className="inline-flex items-center gap-2 bg-white text-teal-dark font-semibold rounded-2xl px-7 py-3 hover:bg-cream-light transition-colors min-h-[48px]"
                    >
                        {t("landing.cta.start")} <ArrowRight size={16} strokeWidth={1.8} />
                    </Link>
                    <button
                        type="button"
                        onClick={() => {
                            const el = document.getElementById("landing-tabs");
                            if (el) {
                                el.scrollIntoView({ behavior: "smooth" });
                                window.dispatchEvent(new CustomEvent("ml-open-book-tab"));
                            } else {
                                openCalendlyPopup();
                            }
                        }}
                        data-testid="final-cta-book"
                        className="inline-flex items-center gap-2 border-2 border-white/80 text-white font-semibold rounded-2xl px-7 py-3 hover:bg-white/10 transition-colors min-h-[48px]"
                    >
                        {t("landing.cta.book")}
                    </button>
                    <Link
                        to="/help-finder"
                        data-testid="final-cta-resources"
                        className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-medium rounded-2xl px-7 py-3 hover:bg-white/10 transition-colors min-h-[48px]"
                    >
                        {t("landing.cta.resources")}
                    </Link>
                </div>
            </div>
        </section>
    );
}

// ============================================================================
// QuickSignInModal — in-place sign-in so caregivers never leave the landing.
// Uses the same auth context as /login, so it sets the token, hydrates the
// session, and fires the GA4 login event identically.
// ============================================================================
export function QuickSignInModal({ open, onClose }) {
    const { t } = useCountry();
    const { login } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");
    const emailRef = React.useRef(null);

    // Focus email when the modal opens; reset state when it closes.
    useEffect(() => {
        if (open) {
            setErr("");
            // Defer focus so the input has rendered.
            setTimeout(() => emailRef.current?.focus(), 30);
        } else {
            setForm({ email: "", password: "" });
            setShowPwd(false);
            setSubmitting(false);
            setErr("");
        }
    }, [open]);

    // ESC closes
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setErr("");
        const email = form.email.trim().toLowerCase();
        const password = form.password;
        if (!email || !password) {
            setErr(t("landing.signin.errEmpty"));
            return;
        }
        setSubmitting(true);
        try {
            const user = await login(email, password);
            // Mirror the same redirect pattern Login.jsx uses: admins to the
            // command center, everyone else to their dashboard.
            const next = user?.role === "admin" ? "/admin?view=command" : "/dashboard";
            onClose?.();
            navigate(next);
        } catch (e2) {
            setErr(formatApiErrorDetail(e2?.response?.data?.detail) || t("landing.signin.errBad"));
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-[#1F2624]/40 backdrop-blur-sm animate-fade-in-up"
            data-testid="quick-signin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="quick-signin-title"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-7 relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close sign in"
                    data-testid="quick-signin-close"
                    className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-cream-light text-[#6E7A74] hover:text-[#1F2624] inline-flex items-center justify-center transition-colors"
                >
                    <X size={18} strokeWidth={1.8} />
                </button>

                <div className="flex items-center gap-2 mb-1">
                    <span className="w-9 h-9 rounded-2xl bg-teal text-white flex items-center justify-center">
                        <Compass size={16} strokeWidth={1.8} />
                    </span>
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">{t("landing.signin.eyebrow")}</p>
                </div>
                <h2 id="quick-signin-title" className="font-heading text-2xl text-[#12776F] leading-tight">
                    {t("landing.signin.title")}
                </h2>
                <p className="text-sm text-[#4C5551] mt-1">
                    {t("landing.signin.sub")}
                </p>

                <form onSubmit={handleSubmit} className="mt-5 space-y-3" data-testid="quick-signin-form">
                    <label className="block">
                        <span className="text-xs font-semibold text-[#4C5551] uppercase tracking-wider">{t("landing.signin.email")}</span>
                        <input
                            ref={emailRef}
                            type="email"
                            autoComplete="email"
                            inputMode="email"
                            required
                            value={form.email}
                            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                            placeholder="you@example.com"
                            data-testid="quick-signin-email"
                            className="ml-input mt-1.5 w-full"
                        />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-[#4C5551] uppercase tracking-wider">{t("landing.signin.password")}</span>
                        <div className="relative mt-1.5">
                            <input
                                type={showPwd ? "text" : "password"}
                                autoComplete="current-password"
                                required
                                value={form.password}
                                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                placeholder="••••••••"
                                data-testid="quick-signin-password"
                                className="ml-input w-full pr-11"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd((v) => !v)}
                                aria-label={showPwd ? "Hide password" : "Show password"}
                                data-testid="quick-signin-toggle-password"
                                className="absolute inset-y-0 right-0 px-3 inline-flex items-center text-[#6E7A74] hover:text-teal-dark"
                            >
                                {showPwd ? <EyeOff size={16} strokeWidth={1.8} /> : <EyeIcon size={16} strokeWidth={1.8} />}
                            </button>
                        </div>
                    </label>

                    {err && (
                        <p className="text-sm text-coral-dark bg-coral-light/60 rounded-xl px-3 py-2" data-testid="quick-signin-error">
                            {err}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="quick-signin-submit"
                        className="ml-btn-primary w-full !min-h-[48px] disabled:opacity-60"
                    >
                        {submitting ? <><Loader2 className="animate-spin" size={16} /> {t("landing.signin.signingIn")}</> : <>{t("landing.signin.submit")} <ArrowRight size={16} strokeWidth={1.8} /></>}
                    </button>
                </form>

                <div className="flex items-center justify-between mt-4 text-xs">
                    <Link
                        to="/login"
                        onClick={onClose}
                        className="text-[#4C5551] hover:text-teal-dark"
                        data-testid="quick-signin-other-options"
                    >
                        {t("landing.signin.otherOptions")}
                    </Link>
                    <Link
                        to="/beta"
                        onClick={onClose}
                        className="text-teal-dark font-semibold hover:underline"
                        data-testid="quick-signin-no-account"
                    >
                        {t("landing.signin.noAccount")}
                    </Link>
                </div>
            </div>
        </div>
    );
}


// ============================================================================
// LanguageSwitcher — compact country + language picker for the header.
// Lives at the very top right (next to Contact / Sign in / Try) so visitors
// from any country can immediately set their context. Picking a country
// auto-suggests its default language; both can be overridden independently.
// ============================================================================
function LanguageSwitcher() {
    const { country, language, setCountry, setLanguage, t } = useCountry();
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);
    const meta = countryMeta(country);

    // Click-outside / ESC to close.
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDown);
        window.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const onPickCountry = (code) => {
        setCountry(code);
        // Auto-suggest that country's default language, but only if the
        // visitor hasn't deliberately picked one already this session.
        const c = SUPPORTED_COUNTRIES.find((x) => x.code === code);
        if (c?.defaultLang && c.defaultLang !== language) {
            setLanguage(c.defaultLang);
        }
    };

    return (
        <div className="relative" ref={ref} data-testid="landing-lang-switcher">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                data-testid="landing-lang-switcher-button"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4C5551] hover:text-[#12776F] px-2.5 py-2 rounded-2xl hover:bg-teal-soft/60 transition-colors"
            >
                <span aria-hidden="true" className="text-base leading-none">{meta.flag}</span>
                <span className="hidden sm:inline uppercase tracking-wider text-[11px] font-semibold">{language}</span>
                <ChevronDown size={13} strokeWidth={1.8} className="hidden sm:inline" />
            </button>

            {open && (
                <div
                    className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-cream-dark z-40 overflow-hidden"
                    role="listbox"
                    data-testid="landing-lang-switcher-menu"
                >
                    <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                        {t("common.country")}
                    </div>
                    <div className="max-h-56 overflow-y-auto" data-testid="landing-lang-country-list">
                        {SUPPORTED_COUNTRIES.map((c) => (
                            <button
                                key={c.code}
                                type="button"
                                onClick={() => onPickCountry(c.code)}
                                data-testid={`landing-lang-country-${c.code}`}
                                className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left hover:bg-cream-light transition-colors ${c.code === country ? "bg-teal-soft/40 text-teal-dark font-semibold" : "text-[#1F2624]"}`}
                            >
                                <span aria-hidden="true" className="text-lg leading-none">{c.flag}</span>
                                <span className="flex-1">{c.label}</span>
                                {c.code === country && <Check size={14} strokeWidth={2.2} className="text-teal-dark" />}
                            </button>
                        ))}
                    </div>
                    <div className="border-t border-cream-dark/60 px-4 pt-3 pb-2 text-[10px] uppercase tracking-wider font-semibold text-[#6E7A74]">
                        {t("common.language")}
                    </div>
                    <div className="pb-2" data-testid="landing-lang-language-list">
                        {SUPPORTED_LANGUAGES.map((l) => (
                            <button
                                key={l.code}
                                type="button"
                                onClick={() => { setLanguage(l.code); setOpen(false); }}
                                data-testid={`landing-lang-language-${l.code}`}
                                className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left hover:bg-cream-light transition-colors ${l.code === language ? "bg-teal-soft/40 text-teal-dark font-semibold" : "text-[#1F2624]"}`}
                            >
                                <span className="uppercase tracking-wider text-[10px] font-semibold w-6">{l.code}</span>
                                <span className="flex-1">{l.label}</span>
                                {l.code === language && <Check size={14} strokeWidth={2.2} className="text-teal-dark" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}


// ============================================================================
// InlineSignIn — sign-in form visible directly on the landing (no modal).
// Same auth path as QuickSignInModal + /login: useAuth().login() → role-based
// redirect. Compact horizontal layout on desktop, stacks vertically on mobile.
// ============================================================================
function InlineSignIn() {
    const { t } = useCountry();
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ email: "", password: "" });
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState("");

    // Hide the form once the visitor is already signed in — no point in
    // asking them to log in again.
    if (user && user !== false) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setErr("");
        const email = form.email.trim().toLowerCase();
        const password = form.password;
        if (!email || !password) {
            setErr(t("landing.signin.errEmpty"));
            return;
        }
        setSubmitting(true);
        try {
            const u = await login(email, password);
            navigate(u?.role === "admin" ? "/admin?view=command" : "/dashboard");
        } catch (e2) {
            setErr(formatApiErrorDetail(e2?.response?.data?.detail) || t("landing.signin.errBad"));
            setSubmitting(false);
        }
    };

    return (
        <section
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 md:pb-10"
            data-testid="landing-inline-signin"
        >
            <form
                onSubmit={handleSubmit}
                className="ml-card p-4 md:p-5 bg-white border border-cream-dark/80"
                data-testid="landing-inline-signin-form"
            >
                <div className="flex items-start md:items-center gap-3 md:gap-4 flex-col md:flex-row">
                    {/* Eyebrow */}
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="w-8 h-8 rounded-xl bg-teal-soft text-teal-dark flex items-center justify-center">
                            <KeyRound size={14} strokeWidth={1.8} />
                        </span>
                        <div className="leading-tight">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark">
                                {t("landing.signin.eyebrow")}
                            </p>
                            <p className="text-sm font-heading font-semibold text-[#1F2624]">
                                {t("landing.signin.title")}
                            </p>
                        </div>
                    </div>

                    {/* Fields + button */}
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 w-full">
                        <label className="block">
                            <span className="sr-only">{t("landing.signin.email")}</span>
                            <input
                                type="email"
                                autoComplete="email"
                                inputMode="email"
                                required
                                value={form.email}
                                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                placeholder={t("landing.signin.email")}
                                data-testid="landing-inline-signin-email"
                                className="ml-input w-full !min-h-[44px]"
                            />
                        </label>
                        <label className="block relative">
                            <span className="sr-only">{t("landing.signin.password")}</span>
                            <input
                                type={showPwd ? "text" : "password"}
                                autoComplete="current-password"
                                required
                                value={form.password}
                                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                placeholder={t("landing.signin.password")}
                                data-testid="landing-inline-signin-password"
                                className="ml-input w-full !min-h-[44px] pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPwd((v) => !v)}
                                aria-label={showPwd ? "Hide password" : "Show password"}
                                data-testid="landing-inline-signin-toggle-password"
                                className="absolute inset-y-0 right-0 px-3 inline-flex items-center text-[#6E7A74] hover:text-teal-dark"
                            >
                                {showPwd ? <EyeOff size={15} strokeWidth={1.8} /> : <EyeIcon size={15} strokeWidth={1.8} />}
                            </button>
                        </label>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="landing-inline-signin-submit"
                            className="ml-btn-primary !min-h-[44px] !px-5 disabled:opacity-60"
                        >
                            {submitting ? (
                                <><Loader2 className="animate-spin" size={14} /> {t("landing.signin.signingIn")}</>
                            ) : (
                                <>{t("landing.signin.submit")} <ArrowRight size={14} strokeWidth={1.8} /></>
                            )}
                        </button>
                    </div>
                </div>
                {err && (
                    <p
                        className="text-sm text-coral-dark bg-coral-light/60 rounded-xl px-3 py-2 mt-3"
                        data-testid="landing-inline-signin-error"
                    >
                        {err}
                    </p>
                )}
            </form>
        </section>
    );
}

